/**
 * Issued Tickets — Digital Companion complaint tickets list (Conversations page shell).
 * Shown only when account ticketing is enabled.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Search, Ticket, RefreshCw, Sparkles, Copy } from 'lucide-react';
import { complaintTicketsAPI } from '../../services/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';

export type ComplaintTicketRow = {
  id: number;
  ticket_number: string;
  category_id: string;
  category_label?: string | null;
  status: string;
  priority?: string | null;
  sla_due_at?: string | null;
  assigned_agent_id?: number | null;
  collected_answers?: Record<string, string>;
  events?: Array<{
    id: number;
    event_type: string;
    created_at?: string | null;
    payload?: Record<string, any>;
  }>;
  summary?: string | null;
  persona_name?: string | null;
  resolution_mode?: string;
  assigned_agent?: { id: number; name: string; email: string } | null;
  contact?: { id: number; name?: string; phone_number?: string } | null;
  conversation_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'waiting_on_customer', label: 'Waiting on customer' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Lower sorts first — an open ticket always outranks a closed one so the
// default view surfaces what still needs attention.
const STATUS_PRIORITY: Record<string, number> = {
  open: 0,
  in_progress: 1,
  waiting_on_customer: 2,
  resolved: 3,
  closed: 4,
  cancelled: 5,
};

const STATUS_PILL_CLASSES: Record<string, string> = {
  open: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  waiting_on_customer: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  resolved: 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300',
  closed: 'bg-slate-100 text-slate-500 dark:bg-slate-500/10 dark:text-slate-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
};

const STATUS_BORDER_CLASSES: Record<string, string> = {
  open: 'border-l-emerald-500',
  in_progress: 'border-l-blue-500',
  waiting_on_customer: 'border-l-amber-500',
  resolved: 'border-l-slate-400',
  closed: 'border-l-slate-300 dark:border-l-slate-600',
  cancelled: 'border-l-red-400',
};

const StatusPill: React.FC<{ status: string; className?: string }> = ({ status, className }) => (
  <span
    className={cn(
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize',
      STATUS_PILL_CLASSES[status] || 'bg-muted text-muted-foreground',
      className
    )}
  >
    {status.replace(/_/g, ' ')}
  </span>
);

// 2026-08-08 (§4.6): `priority` used to be a field that existed on every
// ticket but nothing ever set or displayed — the "Needs attention first"
// sort actually sorted by status alone. Real, closed value set now
// (backend: ComplaintTicketPriority), lower weight = more urgent = sorts
// first, matching STATUS_PRIORITY's own convention.
const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const PRIORITY_WEIGHT: Record<string, number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

const PRIORITY_PILL_CLASSES: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
  normal: 'bg-slate-100 text-slate-600 dark:bg-slate-500/10 dark:text-slate-400',
  low: 'bg-slate-100 text-slate-500 dark:bg-slate-500/10 dark:text-slate-400',
};

// §4.5: derived, never stored — mirrors app.services.sla_service.sla_state_for.
// A cached column would just be one more place for state to drift from
// reality; on_track/at_risk/breached is trivial to compute at render time.
const slaStateFor = (
  createdAt?: string | null,
  slaDueAt?: string | null,
  atRiskFraction = 0.8
): 'on_track' | 'at_risk' | 'breached' | null => {
  if (!slaDueAt) return null;
  const due = new Date(slaDueAt).getTime();
  if (Number.isNaN(due)) return null;
  const now = Date.now();
  if (now >= due) return 'breached';
  if (!createdAt) return 'on_track';
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return 'on_track';
  const totalWindow = due - created;
  if (totalWindow <= 0) return 'on_track';
  const elapsed = now - created;
  return elapsed >= atRiskFraction * totalWindow ? 'at_risk' : 'on_track';
};

const SLA_BADGE_CLASSES: Record<string, string> = {
  on_track: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  at_risk: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  breached: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
};

const SLA_BADGE_LABEL: Record<string, string> = {
  on_track: 'On track',
  at_risk: 'At risk',
  breached: 'SLA breached',
};

// on_track isn't worth a badge on every row — only surface when it needs attention.
const SLABadge: React.FC<{ createdAt?: string | null; slaDueAt?: string | null }> = ({
  createdAt,
  slaDueAt,
}) => {
  const state = slaStateFor(createdAt, slaDueAt);
  if (!state || state === 'on_track') return null;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        SLA_BADGE_CLASSES[state]
      )}
    >
      {SLA_BADGE_LABEL[state]}
    </span>
  );
};

// Normal/unset priority isn't worth a visual flag on every row — only
// surface the pill when it's actually elevated (or explicitly low), same
// "don't show a badge for the default case" instinct as StatusPill's own
// muted fallback.
const PriorityPill: React.FC<{ priority?: string | null; className?: string }> = ({
  priority,
  className,
}) => {
  if (!priority || priority === 'normal') return null;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize',
        PRIORITY_PILL_CLASSES[priority] || 'bg-muted text-muted-foreground',
        className
      )}
    >
      {priority}
    </span>
  );
};

// Deterministic accent per category/person name — same string always lands
// on the same hue, so the same category or agent reads consistently as you
// scan down the table without needing a legend.
const ACCENT_HUES = [
  { dot: 'bg-teal-500', text: 'text-teal-700 dark:text-teal-400' },
  { dot: 'bg-indigo-500', text: 'text-indigo-700 dark:text-indigo-400' },
  { dot: 'bg-rose-500', text: 'text-rose-700 dark:text-rose-400' },
  { dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400' },
  { dot: 'bg-violet-500', text: 'text-violet-700 dark:text-violet-400' },
  { dot: 'bg-cyan-600', text: 'text-cyan-700 dark:text-cyan-400' },
  { dot: 'bg-emerald-600', text: 'text-emerald-700 dark:text-emerald-400' },
  { dot: 'bg-fuchsia-500', text: 'text-fuchsia-700 dark:text-fuchsia-400' },
];

const accentFor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return ACCENT_HUES[hash % ACCENT_HUES.length];
};

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
};

const NameAvatar: React.FC<{ name: string; unassigned?: boolean }> = ({ name, unassigned }) => {
  if (unassigned) {
    return (
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/40 text-[10px] text-muted-foreground">
        —
      </span>
    );
  }
  const accent = accentFor(name);
  return (
    <span
      className={cn(
        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white',
        accent.dot
      )}
    >
      {initials(name)}
    </span>
  );
};

const formatUser = (ticket: ComplaintTicketRow): string => {
  const name = ticket.contact?.name?.trim();
  if (name) return name;
  const phone = ticket.contact?.phone_number?.trim();
  if (phone) return `WhatsApp User (${phone.slice(-4)})`;
  return 'Unknown user';
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const timeAgo = (iso?: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

// A still-open ticket getting old is the one thing this list should make
// impossible to miss.
const isStale = (ticket: ComplaintTicketRow): boolean => {
  if (ticket.status !== 'open' && ticket.status !== 'in_progress') return false;
  if (!ticket.created_at) return false;
  const date = new Date(ticket.created_at);
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() > 24 * 60 * 60 * 1000;
};

type SortMode = 'priority' | 'newest' | 'oldest';

const SORT_OPTIONS: Array<{ value: SortMode; label: string }> = [
  { value: 'priority', label: 'Needs attention first' },
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
];

type Props = {
  highlightTicketNumber?: string | null;
  onOpenConversation?: (conversationId: number) => void;
};

const IssuedTicketsPanel: React.FC<Props> = ({
  highlightTicketNumber,
  onOpenConversation,
}) => {
  const [tickets, setTickets] = useState<ComplaintTicketRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortMode, setSortMode] = useState<SortMode>('priority');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsId, setDetailsId] = useState<number | null>(null);
  const [detailsTicket, setDetailsTicket] = useState<ComplaintTicketRow | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [suggestingReply, setSuggestingReply] = useState(false);
  const [suggestedReply, setSuggestedReply] = useState<string | null>(null);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [agents, setAgents] = useState<Array<{
    id: number;
    name: string;
    notify_eligible: boolean;
  }>>([]);

  const openDetails = useCallback(async (ticket: ComplaintTicketRow) => {
    setDetailsId(ticket.id);
    setDetailsTicket(ticket);
    setSuggestedReply(null);
    setSuggestError(null);
    setCommentDraft('');
    setDetailsLoading(true);
    try {
      const detail = await complaintTicketsAPI.get(ticket.id);
      setDetailsTicket(detail);
    } catch {
      // Row data is still useful if the detail refresh fails.
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const closeDetails = () => {
    setDetailsId(null);
    setDetailsTicket(null);
  };

  // Fetches every status in one page and filters client-side (same as
  // search) — that's what lets the status chips below show a true
  // all-statuses breakdown instead of only whichever one is currently
  // selected, and it saves a round-trip every time the filter changes.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await complaintTicketsAPI.list({ limit: 100 });
      setTickets(res.tickets || []);
      setTotal(res.total || 0);
      if (highlightTicketNumber) {
        const hit = (res.tickets || []).find(
          (t: ComplaintTicketRow) => t.ticket_number === highlightTicketNumber
        );
        if (hit) openDetails(hit);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to load issued tickets';
      setError(typeof msg === 'string' ? msg : 'Failed to load issued tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightTicketNumber]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // 2026-08-16: was getConfigOptions() (admin-only) — confirmed a real
    // gap, a non-admin agent's call 403'd, silently caught below, and the
    // whole reassign control (gated on agents.length > 0) just never
    // appeared for them. assignable-agents is the same list, open to any
    // active user.
    complaintTicketsAPI
      .assignableAgents()
      .then((res) => setAgents(res.agents || []))
      .catch(() => setAgents([]));
  }, []);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    tickets.forEach((t) => {
      counts[t.status] = (counts[t.status] || 0) + 1;
    });
    return counts;
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = tickets;
    if (statusFilter !== 'all') {
      rows = rows.filter((t) => t.status === statusFilter);
    }
    if (q) {
      rows = rows.filter((t) => {
        const haystack = [
          t.ticket_number,
          t.contact?.name,
          t.contact?.phone_number,
          t.category_label,
          t.category_id,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    const sorted = [...rows];
    sorted.sort((a, b) => {
      if (sortMode === 'priority') {
        // Real priority now genuinely drives "needs attention first" — it
        // used to silently fall through to status-only ordering (§4.6).
        const priorityDiff =
          (PRIORITY_WEIGHT[a.priority || 'normal'] ?? 2) -
          (PRIORITY_WEIGHT[b.priority || 'normal'] ?? 2);
        if (priorityDiff !== 0) return priorityDiff;
        const statusDiff = (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9);
        if (statusDiff !== 0) return statusDiff;
      }
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortMode === 'oldest' ? aTime - bTime : bTime - aTime;
    });
    return sorted;
  }, [tickets, statusFilter, search, sortMode]);

  const handleStatusChange = async (ticketId: number, status: string) => {
    setUpdating(true);
    try {
      const updated = await complaintTicketsAPI.updateStatus(ticketId, { status });
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, ...updated } : t)));
      setDetailsTicket((prev) => (prev?.id === ticketId ? { ...prev, ...updated } : prev));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handlePriorityChange = async (ticketId: number, priority: string) => {
    setUpdating(true);
    try {
      const updated = await complaintTicketsAPI.updatePriority(ticketId, { priority });
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, ...updated } : t)));
      setDetailsTicket((prev) => (prev?.id === ticketId ? { ...prev, ...updated } : prev));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update priority');
    } finally {
      setUpdating(false);
    }
  };

  // §16 #1: drafts a reply grounded in the ticket + category dataset/RAGFlow
  // for the agent to review and paste into the actual conversation — never
  // sent from here, no new reply-sending pathway.
  const handleSuggestReply = async (ticketId: number) => {
    setSuggestingReply(true);
    setSuggestError(null);
    try {
      const result = await complaintTicketsAPI.suggestReply(ticketId);
      setSuggestedReply(result.suggested_reply);
    } catch (err: any) {
      setSuggestError(err?.response?.data?.detail || 'Could not draft a reply');
    } finally {
      setSuggestingReply(false);
    }
  };

  const handleAddComment = async (ticketId: number) => {
    const text = commentDraft.trim();
    if (!text) return;
    setPostingComment(true);
    try {
      await complaintTicketsAPI.addComment(ticketId, { text });
      setCommentDraft('');
      const detail = await complaintTicketsAPI.get(ticketId);
      setDetailsTicket(detail);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to add comment');
    } finally {
      setPostingComment(false);
    }
  };

  const handleCopySuggestedReply = () => {
    if (suggestedReply) navigator.clipboard?.writeText(suggestedReply).catch(() => {});
  };

  const handleAssignment = async (ticketId: number, agentId: number) => {
    setUpdating(true);
    try {
      const updated = await complaintTicketsAPI.assign(ticketId, {
        agent_id: agentId,
        notify: true,
      });
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, ...updated } : t)));
      setDetailsTicket((prev) => (prev?.id === ticketId ? { ...prev, ...updated } : prev));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to assign ticket');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="relative min-w-[240px] max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by ticket ID, phone, or name..."
            className="h-9 pl-9"
          />
        </div>
        <Select value={sortMode} onValueChange={(v) => setSortMode(v as SortMode)}>
          <SelectTrigger className="h-9 w-[190px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-sm">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon" className="ml-auto h-9 w-9" onClick={load} disabled={loading}>
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-muted/30 px-4 py-2.5">
        <button
          type="button"
          onClick={() => setStatusFilter('all')}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
            statusFilter === 'all'
              ? 'border-primary/40 bg-primary/10 text-primary'
              : 'border-transparent text-muted-foreground hover:bg-muted'
          )}
        >
          All
          <span className="tabular-nums">{total}</span>
        </button>
        {STATUS_OPTIONS.filter((o) => o.value !== 'all' && statusCounts[o.value]).map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => setStatusFilter(o.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium capitalize transition-opacity',
              STATUS_PILL_CLASSES[o.value],
              statusFilter === o.value ? 'ring-1 ring-inset ring-current' : 'opacity-70 hover:opacity-100'
            )}
          >
            {o.label}
            <span className="tabular-nums">{statusCounts[o.value]}</span>
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {loading && tickets.length === 0 ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="px-4 py-10 text-center text-sm text-destructive">{error}</p>
        ) : filteredTickets.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-16 text-center text-sm text-muted-foreground">
            <Ticket className="h-6 w-6 opacity-50" />
            {tickets.length === 0 ? 'No complaint tickets yet' : 'No tickets match your filters'}
          </div>
        ) : (
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[13%]">Ticket ID</TableHead>
                <TableHead className="w-[17%]">User</TableHead>
                <TableHead className="w-[17%]">Issue Raised</TableHead>
                <TableHead className="w-[17%]">Assigned</TableHead>
                <TableHead className="w-[10%]">Status</TableHead>
                <TableHead className="w-[14%]">Date &amp; Time</TableHead>
                <TableHead className="w-[12%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTickets.map((t) => {
                const categoryLabel = t.category_label || t.category_id;
                const categoryAccent = accentFor(categoryLabel);
                const agentName = t.assigned_agent?.name;
                const stale = isStale(t);
                return (
                  <TableRow
                    key={t.id}
                    className={cn(
                      'border-l-4',
                      STATUS_BORDER_CLASSES[t.status] || 'border-l-transparent',
                      highlightTicketNumber === t.ticket_number && 'bg-primary/5'
                    )}
                  >
                    <TableCell className="break-words font-medium tabular-nums">{t.ticket_number}</TableCell>
                    <TableCell>
                      <div className="flex items-start gap-2">
                        <NameAvatar name={formatUser(t)} />
                        <span className="break-words">{formatUser(t)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-start gap-1.5">
                        <span className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', categoryAccent.dot)} />
                        <span className={cn('break-words', categoryAccent.text)}>{categoryLabel}</span>
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-2">
                        <NameAvatar name={agentName || ''} unassigned={!agentName} />
                        <span className={cn('break-words text-sm', !agentName && 'text-muted-foreground')}>
                          {agentName || 'Unassigned'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusPill status={t.status} />
                        <PriorityPill priority={t.priority} />
                        <SLABadge createdAt={t.created_at} slaDueAt={t.sla_due_at} />
                      </div>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">
                      <div className="text-muted-foreground">{formatDateTime(t.created_at)}</div>
                      <div
                        className={cn(
                          'flex items-center gap-1 text-xs',
                          stale ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-muted-foreground/70'
                        )}
                      >
                        {stale && <AlertTriangle className="h-3 w-3" />}
                        {timeAgo(t.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-3 text-sm">
                        <button
                          type="button"
                          className="font-medium text-primary hover:underline"
                          onClick={() => openDetails(t)}
                        >
                          Details
                        </button>
                        {t.conversation_id && onOpenConversation && (
                          <button
                            type="button"
                            className="font-medium text-primary hover:underline"
                            onClick={() => onOpenConversation(t.conversation_id!)}
                          >
                            View Chat
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={detailsId !== null} onOpenChange={(open) => !open && closeDetails()}>
        <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
          {detailsTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  <span className="tabular-nums">{detailsTicket.ticket_number}</span>
                  <StatusPill status={detailsTicket.status} />
                  <PriorityPill priority={detailsTicket.priority} />
                  <SLABadge createdAt={detailsTicket.created_at} slaDueAt={detailsTicket.sla_due_at} />
                  {isStale(detailsTicket) && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="h-3 w-3" /> Open {timeAgo(detailsTicket.created_at)}
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription>
                  {detailsTicket.category_label || detailsTicket.category_id}
                  {detailsTicket.persona_name ? ` · ${detailsTicket.persona_name}` : ''}
                  {detailsTicket.resolution_mode === 'virtual_close' ? ' · Virtual close' : ''}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select
                      value={detailsTicket.status}
                      onValueChange={(v) => handleStatusChange(detailsTicket.id, v)}
                      disabled={updating}
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.filter((o) => o.value !== 'all').map((o) => (
                          <SelectItem key={o.value} value={o.value} className="text-sm">
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Priority</Label>
                    <Select
                      value={detailsTicket.priority || 'normal'}
                      onValueChange={(v) => handlePriorityChange(detailsTicket.id, v)}
                      disabled={updating}
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value} className="text-sm">
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {agents.length > 0 && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Assignee</Label>
                      <Select
                        value={detailsTicket.assigned_agent_id ? String(detailsTicket.assigned_agent_id) : 'unassigned'}
                        onValueChange={(value) => handleAssignment(detailsTicket.id, Number(value))}
                        disabled={updating}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue placeholder="Assign agent" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned" disabled>Unassigned</SelectItem>
                          {agents.map((agent) => (
                            <SelectItem key={agent.id} value={String(agent.id)} className="text-sm">
                              {agent.name}{agent.notify_eligible ? '' : ' · no WhatsApp phone'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <NameAvatar name={formatUser(detailsTicket)} />
                    <div>
                      <p className="text-xs text-muted-foreground">User</p>
                      <p>{formatUser(detailsTicket)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Raised</p>
                    <p className="tabular-nums">{formatDateTime(detailsTicket.created_at)}</p>
                  </div>
                </div>

                {/* Summary is Category/Initial issue/Q&A concatenated into one text
                    blob — the same content Collected details below already shows,
                    laid out better. Only shown when there's nothing else, i.e. no
                    structured Q&A was collected at all. */}
                {detailsTicket.summary &&
                  (!detailsTicket.collected_answers || Object.keys(detailsTicket.collected_answers).length === 0) && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-muted-foreground">Summary</p>
                      <p className="text-sm whitespace-pre-wrap">{detailsTicket.summary}</p>
                    </div>
                  )}

                {detailsTicket.collected_answers && Object.keys(detailsTicket.collected_answers).length > 0 && (
                  <div>
                    <p className="mb-1 text-xs font-medium text-muted-foreground">Collected details</p>
                    <dl className="divide-y divide-border rounded-md border border-border">
                      {Object.entries(detailsTicket.collected_answers).map(([key, answer]) => (
                        <div key={key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3 px-3 py-2">
                          <dt className="text-xs text-muted-foreground">{key}</dt>
                          <dd className="text-sm whitespace-pre-wrap">{answer}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                <div className="space-y-2 rounded-md border border-dashed border-border p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground">Suggested reply</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 gap-1 text-xs"
                      disabled={suggestingReply}
                      onClick={() => handleSuggestReply(detailsTicket.id)}
                    >
                      {suggestingReply ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Sparkles className="h-3 w-3" />
                      )}
                      {suggestedReply ? 'Regenerate' : 'Draft with AI'}
                    </Button>
                  </div>
                  {suggestError && <p className="text-xs text-destructive">{suggestError}</p>}
                  {suggestedReply && (
                    <div className="space-y-1.5">
                      <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-2 text-sm">
                        {suggestedReply}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 text-xs"
                        onClick={handleCopySuggestedReply}
                      >
                        <Copy className="h-3 w-3" />
                        Copy
                      </Button>
                      <p className="text-[11px] text-muted-foreground">
                        AI-drafted — review before sending. Paste it into the conversation to send it.
                      </p>
                    </div>
                  )}
                  {!suggestedReply && !suggestError && !suggestingReply && (
                    <p className="text-xs text-muted-foreground">
                      Draft a reply grounded in this ticket's details for you to review and send.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Add a comment</p>
                  <Textarea
                    rows={2}
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    placeholder="Internal note — visible to other agents on this ticket, not sent to the contact…"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!commentDraft.trim() || postingComment}
                    onClick={() => handleAddComment(detailsTicket.id)}
                  >
                    {postingComment ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Post comment'}
                  </Button>
                </div>

                {detailsTicket.events && detailsTicket.events.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-medium text-muted-foreground">Activity</p>
                    <div className="space-y-2 border-l border-border pl-3">
                      {detailsTicket.events.map((event) => (
                        <div key={event.id}>
                          <p className="text-xs font-medium capitalize">
                            {event.event_type.replace(/_/g, ' ')}
                          </p>
                          {event.event_type === 'comment' && event.payload?.text && (
                            <p className="text-sm whitespace-pre-wrap">{event.payload.text}</p>
                          )}
                          {event.created_at && (
                            <p className="text-[11px] text-muted-foreground">
                              {formatDateTime(event.created_at)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailsLoading && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Refreshing…
                  </div>
                )}
              </div>

              <DialogFooter>
                {detailsTicket.conversation_id && onOpenConversation && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const conversationId = detailsTicket.conversation_id!;
                      closeDetails();
                      onOpenConversation(conversationId);
                    }}
                  >
                    View Chat
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={closeDetails}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IssuedTicketsPanel;
